import { FccPassengerAuthenticationState } from '@backoffice-frontend/graphql'
import type {
  GetPassengersDeliveryActionFragment,
  GetPassengersPickupActionFragment,
  GetPassengersWaypointFragment,
} from './passenger-filters.hook'

export type WaypointAction = GetPassengersWaypointFragment['actions'][number]
export type Trip = NonNullable<GetPassengersDeliveryActionFragment['trip']>
export type Customer = GetPassengersDeliveryActionFragment['customer']

export type RidePassengerInfo =
  GetPassengersDeliveryActionFragment['ridePassengerInfo']

export type PassengerInfo = {
  trip: Trip
  fccStop: GetPassengersWaypointFragment['fccStop'] | null
  stopServiceAreaId: string | null
  customer: Customer | null
  ridePassengerInfo: RidePassengerInfo | null
  authenticationState: FccPassengerAuthenticationState | null
  pickedUpAt: Date | null
  eta: Date
  /** Pick-up time communicated to the passenger. Pickups only. */
  promisedPickupTime: Date | null
  tripIsCancelled?: boolean
  /**
   * The passenger is not coming, either because the trip was cancelled at
   * mission level or because authentication ended in `CANCELLED`. A cancelled
   * trip fires no authentication event, so neither field answers this alone.
   */
  isCancelled: boolean
  nextWaypointActionType: 'PICKUP' | 'DELIVERY'
}

const isPickupAction = (
  action: WaypointAction,
): action is GetPassengersPickupActionFragment & { trip: Trip } => {
  return action.__typename === 'FccWaypointPickupAction' && action.trip !== null
}

const isDeliveryAction = (
  action: WaypointAction,
): action is GetPassengersDeliveryActionFragment & { trip: Trip } => {
  return (
    action.__typename === 'FccWaypointDeliveryAction' && action.trip !== null
  )
}

const getPassengerInfoFromAction = (
  action: (
    | GetPassengersPickupActionFragment
    | GetPassengersDeliveryActionFragment
  ) & { trip: Trip },
  fccStop: GetPassengersWaypointFragment['fccStop'] | null = null,
  stopServiceAreaId: string | null = null,
): PassengerInfo => {
  const baseInfo = {
    trip: action.trip,
    fccStop,
    stopServiceAreaId,
    customer: action.customer,
    ridePassengerInfo: action.ridePassengerInfo ?? null,
    eta: new Date(action.estimatedAt),
    pickedUpAt: action.pickedUpAt ? new Date(action.pickedUpAt) : null,
    authenticationState: isPickupAction(action)
      ? action.adPassengerAuthenticationState
      : null,
  }

  // Only include tripIsCancelled for pickup actions
  if (isPickupAction(action)) {
    const tripIsCancelled = action.tripIsCancelled ?? false

    return {
      ...baseInfo,
      promisedPickupTime: new Date(action.promisedTimeRange.from),
      tripIsCancelled,
      isCancelled:
        tripIsCancelled ||
        action.adPassengerAuthenticationState ===
          FccPassengerAuthenticationState.Cancelled,
      nextWaypointActionType: 'PICKUP',
    }
  }

  return {
    ...baseInfo,
    promisedPickupTime: null,
    isCancelled: false,
    nextWaypointActionType: 'DELIVERY',
  }
}

type TripAction = { __typename?: string; trip?: { id: string } | null }

/**
 * The trips whose passengers are on board: a trip with an upcoming delivery but no upcoming pickup.
 * It reads only the action types and trip ids, so fleet-wide queries can use it.
 */
export const getOnBoardTripIds = (
  upcomingWaypoints: ReadonlyArray<{ actions: ReadonlyArray<TripAction> }>,
): Set<string> => {
  const actions = upcomingWaypoints.flatMap(waypoint => waypoint.actions)
  const upcomingPickupTripIds = new Set(
    actions.flatMap(action =>
      action.__typename === 'FccWaypointPickupAction' && action.trip
        ? [action.trip.id]
        : [],
    ),
  )
  return new Set(
    actions.flatMap(action =>
      action.__typename === 'FccWaypointDeliveryAction' &&
      action.trip &&
      !upcomingPickupTripIds.has(action.trip.id)
        ? [action.trip.id]
        : [],
    ),
  )
}

export const getOnBoardPassengers = (
  upcomingWaypoints: GetPassengersWaypointFragment[],
): PassengerInfo[] => {
  const onBoardTripIds = getOnBoardTripIds(upcomingWaypoints)
  return upcomingWaypoints.flatMap(waypoint =>
    waypoint.actions
      .filter(isDeliveryAction)
      .filter(delivery => onBoardTripIds.has(delivery.trip.id))
      .map(delivery =>
        getPassengerInfoFromAction(
          delivery,
          waypoint.fccStop,
          waypoint.stopWithServiceArea?.serviceArea.id ?? null,
        ),
      ),
  )
}

export const getBoardingPassengers = (
  waypoint: GetPassengersWaypointFragment,
) => {
  const boardingPassengers: PassengerInfo[] = []

  waypoint.actions.forEach(action => {
    if (isPickupAction(action) && action.trip?.id) {
      boardingPassengers.push(
        getPassengerInfoFromAction(
          action,
          waypoint.fccStop,
          waypoint.stopWithServiceArea?.serviceArea.id ?? null,
        ),
      )
    }
  })

  boardingPassengers.sort((a, b) => a.eta.getTime() - b.eta.getTime())

  return boardingPassengers
}

export const getDeboardingPassengers = (
  waypoint: GetPassengersWaypointFragment,
) => {
  const deboardingPassengers: PassengerInfo[] = []

  waypoint.actions.forEach(action => {
    if (isDeliveryAction(action) && action.trip?.id) {
      deboardingPassengers.push(
        getPassengerInfoFromAction(
          action,
          waypoint.fccStop,
          waypoint.stopWithServiceArea?.serviceArea.id ?? null,
        ),
      )
    }
  })

  deboardingPassengers.sort((a, b) => a.eta.getTime() - b.eta.getTime())

  return deboardingPassengers
}

export const getStayingPassengers = (
  waypoint: GetPassengersWaypointFragment,
  futureWaypoints: GetPassengersWaypointFragment[],
) => {
  const boardingPassengers = getBoardingPassengers(waypoint)
  const futurePassengersOnBoard = getOnBoardPassengers(futureWaypoints)

  const stayingPassengers = futurePassengersOnBoard.filter(futurePassenger =>
    boardingPassengers.every(
      boardingPassenger =>
        boardingPassenger.trip.id !== futurePassenger.trip.id,
    ),
  )

  stayingPassengers.sort((a, b) => a.eta.getTime() - b.eta.getTime())

  return stayingPassengers
}

export const getAllPassengers = (
  waypoints: GetPassengersWaypointFragment[],
) => {
  const passengers: PassengerInfo[] = []

  waypoints.forEach(waypoint => {
    waypoint.actions.forEach(action => {
      if (isPickupAction(action) || isDeliveryAction(action)) {
        if (
          !passengers.some(passenger => passenger.trip.id === action.trip.id)
        ) {
          passengers.push(
            getPassengerInfoFromAction(
              action,
              waypoint.fccStop,
              waypoint.stopWithServiceArea?.serviceArea.id ?? null,
            ),
          )
        }
      }
    })
  })

  return passengers
}

export const getPassengerCount = (passengers: PassengerInfo[]) =>
  passengers.reduce(
    (acc, cur) =>
      acc + (cur.ridePassengerInfo?.boardingRequirements?.headCount ?? 0),
    0,
  )
